import { useState, useMemo } from 'react';
import {
  Box, Card, CardContent, Typography, Tabs, Tab, Chip, Stack, alpha,
} from '@mui/material';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import DashboardChartFrame from './DashboardChartFrame';
import { useDashboardChartLayout } from './useDashboardChartLayout';
import { useTenantCurrency } from '../../hooks/useTenantCurrency';

interface ChartSeries {
  _id: string;
  count: number;
  revenue?: number;
}

interface DashboardAnalyticsChartsProps {
  analytics?: Record<string, unknown>;
  loading?: boolean;
  embedded?: boolean;
}

const TABS = [
  { key: 'rideStats', label: 'Transport', color: '#2563eb' },
  { key: 'porterStats', label: 'Porter', color: '#0f766e' },
  { key: 'onDemandStats', label: 'Services', color: '#0891b2' },
] as const;

function mergeChartData(series: ChartSeries[]) {
  return series.map((r) => ({
    date: r._id,
    count: r.count,
    revenue: r.revenue ?? 0,
  }));
}

export default function DashboardAnalyticsCharts({ analytics, loading, embedded }: DashboardAnalyticsChartsProps) {
  const layout = useDashboardChartLayout();
  const { formatAmount } = useTenantCurrency();
  const [tab, setTab] = useState(0);
  const activeKey = TABS[tab]?.key || 'rideStats';

  const chartData = useMemo(() => {
    const series = (analytics?.[activeKey] as ChartSeries[]) || [];
    return mergeChartData(series);
  }, [analytics, activeKey]);

  const totals = useMemo(() => {
    const count = chartData.reduce((s, r) => s + r.count, 0);
    const revenue = chartData.reduce((s, r) => s + r.revenue, 0);
    return { count, revenue };
  }, [chartData]);

  const color = TABS[tab]?.color || '#2563eb';

  const content = (
    <Box sx={{ width: '100%', minWidth: 0 }}>
      <Box
        display="flex"
        alignItems="flex-start"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={1}
        mb={2}
      >
        {!embedded && (
          <Box minWidth={0} flex="1 1 200px">
            <Typography variant="subtitle1" fontWeight={800}>30-day service trends</Typography>
            <Typography variant="caption" color="text.secondary">
              Volume and revenue per vertical — switch tabs to drill down
            </Typography>
          </Box>
        )}
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ ml: embedded ? 0 : 'auto' }}>
          <Chip size="small" label={`${totals.count} volume`} sx={{ fontWeight: 700 }} />
          <Chip size="small" label={formatAmount(totals.revenue)} color="success" variant="outlined" sx={{ fontWeight: 700 }} />
        </Stack>
      </Box>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{ mb: 2, minHeight: 36, '& .MuiTab-root': { minHeight: 36, py: 0.5, fontWeight: 700, fontSize: '0.8rem' } }}
      >
        {TABS.map((t) => (
          <Tab key={t.key} label={t.label} />
        ))}
      </Tabs>

      <DashboardChartFrame loading={loading} size={embedded ? 'md' : 'sm'}>
        {chartData.length ? (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={layout.margin}>
              <defs>
                <linearGradient id="countGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={color} stopOpacity={0.25} />
                  <stop offset="95%" stopColor={color} stopOpacity={0} />
                </linearGradient>
                <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#059669" stopOpacity={0.2} />
                  <stop offset="95%" stopColor="#059669" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={alpha('#64748b', 0.2)} />
              <XAxis
                dataKey="date"
                tick={layout.axis.tick}
                angle={layout.axis.x.angle}
                textAnchor={layout.axis.x.textAnchor}
                height={layout.axis.x.height}
                interval={layout.axis.x.interval}
                tickFormatter={layout.formatDateTick}
              />
              <YAxis yAxisId="left" tick={layout.axis.tick} width={layout.axis.y.width} />
              <YAxis yAxisId="right" orientation="right" tick={layout.axis.tick} width={layout.axis.y.width} />
              <Tooltip
                {...layout.tooltip}
                formatter={(value: number, name: string) =>
                  name === 'revenue' ? [formatAmount(value), 'Revenue'] : [value, 'Volume']
                }
              />
              <Legend {...layout.legend} />
              <Area yAxisId="left" type="monotone" dataKey="count" name="Volume" stroke={color} fill="url(#countGrad)" strokeWidth={2} />
              <Area yAxisId="right" type="monotone" dataKey="revenue" name="Revenue" stroke="#059669" fill="url(#revGrad)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <Box display="flex" alignItems="center" justifyContent="center" height="100%">
            <Typography variant="body2" color="text.secondary">No trend data for this period</Typography>
          </Box>
        )}
      </DashboardChartFrame>
    </Box>
  );

  if (embedded) return content;

  return (
    <Card elevation={0} sx={{ border: 1, borderColor: 'divider', borderRadius: 3, mb: 3 }}>
      <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
        {content}
      </CardContent>
    </Card>
  );
}
